/**
 * Language tabs for the Resources page (and any [data-component="resources-lang"] region).
 *
 */

const ROOT_SELECTOR = '[data-component="resources-lang"]'
const BUTTON_SELECTOR = '.language-btn[data-lang]'

const getButtons = root => Array.from(root.querySelectorAll(BUTTON_SELECTOR))
const getPanels = root => Array.from(root.querySelectorAll('[data-lang-panel]'))

/**
 * A language is only offered if its panel actually has cards to show.
 */
function hasPanelContent(panel) {
  return Boolean(panel) && panel.querySelectorAll('.resources-card').length > 0
}

function getSecondaryPanel(root) {
  return root.querySelector('[data-lang-panel="es"]')
}

function isToggleEnabled(root) {
  return hasPanelContent(getSecondaryPanel(root))
}

function updateUrlParam(lang) {
  const params = new URLSearchParams(window.location.search)

  if (lang && lang !== 'en') {
    params.set('lang', lang)
  }
  else {
    params.delete('lang')
  }

  const qs = params.toString()
  const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
  window.history.replaceState(null, '', newUrl)
}

function switchLanguage(root, lang, shouldFocus = false) {
  if (!root || !lang) {
    return
  }

  updateUrlParam(lang)

  getButtons(root).forEach((btn) => {
    const active = btn.dataset.lang === lang
    btn.classList.toggle('active', active)
    btn.setAttribute('aria-selected', active ? 'true' : 'false')
    btn.setAttribute('tabindex', active ? '0' : '-1')
    if (active && shouldFocus) {
      btn.focus()
    }
  })

  getPanels(root).forEach((panel) => {
    const show = panel.dataset.langPanel === lang
    panel.hidden = !show
    panel.classList.toggle('resources-section__cards--active', show)
    if (show) {
      panel.removeAttribute('aria-hidden')
    }
    else {
      panel.setAttribute('aria-hidden', 'true')
    }
  })
}

/**
 * Hides the toggle and pins the English panel when there is no Spanish content.
 */
function disableLanguageToggle(root) {
  const toggle = root.querySelector('.resources-section__lang-toggle, .language-toggle')
  const enPanel = root.querySelector('[data-lang-panel="en"]')
  const esPanel = getSecondaryPanel(root)

  if (toggle) {
    toggle.hidden = true
    toggle.setAttribute('aria-hidden', 'true')
    toggle.setAttribute('inert', '')
  }

  if (esPanel) {
    esPanel.hidden = true
    esPanel.classList.remove('resources-section__cards--active')
    esPanel.setAttribute('aria-hidden', 'true')
  }

  if (enPanel) {
    enPanel.hidden = false
    enPanel.classList.add('resources-section__cards--active')
    enPanel.removeAttribute('aria-hidden')
  }
}

function onDelegatedClick(e) {
  const btn = e.target.closest ? e.target.closest(BUTTON_SELECTOR) : null
  if (!btn) {
    return
  }

  const root = btn.closest(ROOT_SELECTOR)
  if (!root || !isToggleEnabled(root)) {
    return
  }

  e.preventDefault()
  switchLanguage(root, btn.dataset.lang, false)
}

function onDelegatedKeydown(e) {
  const btn = e.target.closest ? e.target.closest(BUTTON_SELECTOR) : null
  if (!btn) {
    return
  }

  const root = btn.closest(ROOT_SELECTOR)
  if (!root || !isToggleEnabled(root)) {
    return
  }

  const list = getButtons(root)
  const i = list.indexOf(btn)
  let next = null

  switch (e.key) {
  case 'ArrowLeft':
  case 'ArrowUp':
    e.preventDefault()
    next = list[i - 1] || list[list.length - 1]
    break
  case 'ArrowRight':
  case 'ArrowDown':
    e.preventDefault()
    next = list[i + 1] || list[0]
    break
  case 'Home':
    e.preventDefault()
    next = list[0]
    break
  case 'End':
    e.preventDefault()
    next = list[list.length - 1]
    break
  // Enter/Space on a <button> already fire a click event, which the delegated
  // click handler picks up. Handling them here too would double-switch.
  default:
    return
  }

  if (next) {
    switchLanguage(root, next.dataset.lang, true)
  }
}

let delegationInstalled = false

function installDelegation() {
  if (delegationInstalled) {
    return
  }
  delegationInstalled = true

  document.addEventListener('click', onDelegatedClick)
  document.addEventListener('keydown', onDelegatedKeydown)
}

/**
 * Applies the initial language state to a region. Idempotent.
 */
function initRoot(root) {
  if (!root || root.dataset.initialized === 'true') {
    return
  }

  if (!isToggleEnabled(root)) {
    disableLanguageToggle(root)
    root.dataset.initialized = 'true'
    return
  }

  const buttons = getButtons(root)
  if (!buttons.length || !getPanels(root).length) {
    return
  }

  root.dataset.initialized = 'true'

  const urlLang = new URLSearchParams(window.location.search).get('lang')
  const validLangs = buttons.map(b => b.dataset.lang)
  const initialLang = (urlLang && validLangs.includes(urlLang))
    ? urlLang
    : (root.querySelector('.language-btn.active')?.dataset.lang || 'en')

  switchLanguage(root, initialLang, false)
}

function initAll(context = document) {
  const scope = context && context.querySelectorAll ? context : document

  if (scope.matches && scope.matches(ROOT_SELECTOR)) {
    initRoot(scope)
  }

  scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot)
}

// Install delegation as soon as this module is evaluated. Clicks then work even
// if every other initializer on the page fails; initRoot only sets up the
// initial visible state.
installDelegation()

if (typeof window !== 'undefined' && window.Drupal && window.Drupal.behaviors) {
  window.Drupal.behaviors.tryngolzaResourcesLang = {
    attach(context) {
      initAll(context)
    }
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document))
  }
  else {
    initAll(document)
  }
}

/**
 * Retained so existing call sites (`new ResourcesLang(el)`) keep working.
 */
class ResourcesLang {
  constructor(root) {
    initRoot(root)
  }
}

export default ResourcesLang
export { initAll, initRoot, switchLanguage }
